import React, { Component } from 'react';
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import moment from 'moment';
import { toast } from 'react-toastify';

import PageTitle from '../../../components/common/PageTitle';
import Chart from '../../../utils/chart';
import history from '../../../utils/history';
import FeatherIcon from 'feather-icons-react';
import {
  Button, ButtonGroup,
  Col, Row,
  Card, CardHeader, CardBody,
  FormSelect, FormInput, Modal, ModalHeader, ModalBody, Badge
} from 'shards-react';

// cafe24_webhook.md 기준 — event_no 별 공식 이벤트명 (카페24 표준 라벨).
const CAFE24_EVENT_NAMES = {
  // 상품
  90001: '쇼핑몰에 상품이 등록된 경우',
  90002: '쇼핑몰 상품이 수정된 경우',
  90003: '쇼핑몰 상품이 삭제된 경우',
  90022: '쇼핑몰에서 상품을 복구한 경우',
  90041: '쇼핑몰 상품이 일괄 수정된 경우',
  90075: '쇼핑몰 품목 재고가 변동된 경우',
  90150: '쇼핑몰 상품 품절 상태가 변경된 경우',
  // 상품분류
  90042: '쇼핑몰에 상품분류가 추가된 경우',
  90043: '쇼핑몰의 상품분류가 수정된 경우',
  90044: '쇼핑몰에서 상품분류가 삭제된 경우',
  90045: '쇼핑몰 상품분류의 순서가 변경된 경우',
  90046: '쇼핑몰에서 상품분류의 상품 진열 설정을 일괄변경한 경우',
  // 게시판
  90033: '쇼핑몰에 게시물이 등록된 경우',
  90034: '쇼핑몰 게시물에 댓글이 등록된 경우',
  90035: '쇼핑몰에 긴급문의가 접수된 경우',
  90036: '쇼핑몰의 게시물이 삭제된 경우',
  90037: '쇼핑몰 게시물의 댓글이 삭제된 경우',
  90038: '쇼핑몰에 접수된 긴급문의가 삭제된 경우',
  90039: '쇼핑몰의 게시물이 수정된 경우',
  // 주문
  90023: '쇼핑몰에 주문이 접수된 경우',
  90024: '쇼핑몰에 접수된 주문의 배송상태가 변경된 경우',
  90025: '쇼핑몰에 접수된 주문의 입금상태가 변경된 경우',
  90026: '쇼핑몰에 접수된 주문의 취소상태가 변경된 경우',
  90027: '쇼핑몰에 접수된 주문의 반품상태가 변경된 경우',
  90028: '쇼핑몰에 접수된 주문의 교환상태가 변경된 경우',
  90029: '쇼핑몰에 접수된 주문의 환불상태가 변경된 경우',
  90031: '쇼핑몰에 접수된 주문에 상품을 추가한 경우',
  90064: '쇼핑몰에 접수된 주문의 수령자정보가 변경된 경우',
  90066: '쇼핑몰에 접수된 주문에 관리자메모가 등록된 경우',
  90068: '쇼핑몰에 접수된 주문에 관리자메모가 수정된 경우',
  90069: '쇼핑몰에 접수된 주문에 관리자메모가 삭제된 경우',
  90070: '쇼핑몰에서 주문서가 삭제된 경우',
  90071: '쇼핑몰에 접수된 주문의 배송상태가 변경된 경우 (일괄)',
  90072: '쇼핑몰에 접수된 주문의 취소상태가 변경된 경우 (일괄)',
  90073: '쇼핑몰에 접수된 주문의 환불상태가 변경된 경우 (일괄)',
  90074: '쇼핑몰에 접수된 주문의 반품상태가 변경된 경우 (일괄)',
  90084: '쇼핑몰 상품이 장바구니에 담긴 경우',
  90160: '쇼핑몰에 접수된 주문의 반품이 완료된 경우 (취소번호별)',
  90162: '쇼핑몰에 접수된 주문의 송장번호가 변경된 경우',
  90186: '쇼핑몰에 접수된 주문의 취소상태가 변경된 경우 (품목별)',
  90188: '쇼핑몰에 접수된 주문에 상품을 추가한 경우 (품목별)',
  // 앱
  90077: '쇼핑몰에 설치된 앱이 삭제된 경우',
  90078: '쇼핑몰에 설치된 앱이 만료된 경우',
  90079: '쇼핑몰에 설치된 앱의 만료일이 연장된 경우',
  90157: '쇼핑몰에 설치된 앱이 결제된 경우',
  90158: '쇼핑몰에 설치된 앱의 결제 환불을 요청한 경우',
  90159: '쇼핑몰에 설치된 앱의 결제 환불이 완료된 경우',
};

// cafe24_webhook.md 기준 — event_no 단위 카탈로그.
// 카페24 어드민(앱 설정 > Webhook)에 각 event_no 별로 등록해야 호출이 도달함.
// tier: A=기존 핸들러 / B=신규 핸들러 / PAY=결제 보류 / TODO=미구현(코드 없음)
//   path 가 null 이면 코드 핸들러 없음 (TODO)
const HANDLER_CATALOG = [
  // ---- 상품 ----
  { group: '상품', event_no: 90001, path: '/cafe24wh/product',           tier: 'A',    label: '상품 등록' },
  { group: '상품', event_no: 90002, path: '/cafe24wh/product',           tier: 'A',    label: '상품 수정' },
  { group: '상품', event_no: 90003, path: '/cafe24wh/product/del',       tier: 'A',    label: '상품 삭제' },
  { group: '상품', event_no: 90022, path: '/cafe24wh/product',           tier: 'A',    label: '상품 복구' },
  { group: '상품', event_no: 90041, path: '/cafe24wh/product',           tier: 'A',    label: '상품 일괄 수정' },
  { group: '상품', event_no: 90042, path: '/cafe24wh/category',          tier: 'B',    label: '상품분류 추가' },
  { group: '상품', event_no: 90043, path: '/cafe24wh/category',          tier: 'B',    label: '상품분류 수정' },
  { group: '상품', event_no: 90044, path: '/cafe24wh/category',          tier: 'B',    label: '상품분류 삭제' },
  { group: '상품', event_no: 90045, path: '/cafe24wh/category',          tier: 'B',    label: '상품분류 순서 변경' },
  { group: '상품', event_no: 90046, path: '/cafe24wh/category',          tier: 'B',    label: '상품 진열 일괄변경' },
  { group: '상품', event_no: 90075, path: '/cafe24wh/product/stock',     tier: 'B',    label: '품목 재고 변동' },
  { group: '상품', event_no: 90150, path: '/cafe24wh/product/sold_out',  tier: 'B',    label: '상품 품절 상태 변경' },

  // ---- 게시판 ----
  { group: '게시판', event_no: 90033, path: '/cafe24wh/article',         tier: 'A',    label: '게시물(리뷰) 등록' },
  { group: '게시판', event_no: 90034, path: '/cafe24wh/comment',         tier: 'B',    label: '게시물 댓글(답글) 등록' },
  { group: '게시판', event_no: 90035, path: null,                        tier: 'TODO', label: '긴급문의 접수 (미구현)' },
  { group: '게시판', event_no: 90036, path: '/cafe24wh/article/del',     tier: 'A',    label: '게시물 삭제' },
  { group: '게시판', event_no: 90037, path: '/cafe24wh/comment/del',     tier: 'B',    label: '게시물 댓글 삭제' },
  { group: '게시판', event_no: 90038, path: null,                        tier: 'TODO', label: '긴급문의 삭제 (미구현)' },
  { group: '게시판', event_no: 90039, path: '/cafe24wh/article/update',  tier: 'A',    label: '게시물 수정' },

  // ---- 주문 ----
  { group: '주문', event_no: 90023, path: '/cafe24wh/order/new',  tier: 'A',    label: '주문 접수' },
  { group: '주문', event_no: 90024, path: '/cafe24wh/order/new',  tier: 'A',    label: '주문 배송상태 변경' },
  { group: '주문', event_no: 90025, path: '/cafe24wh/order/new',  tier: 'A',    label: '주문 입금상태 변경' },
  { group: '주문', event_no: 90026, path: '/cafe24wh/order/new',  tier: 'A',    label: '주문 취소상태 변경' },
  { group: '주문', event_no: 90027, path: '/cafe24wh/order/new',  tier: 'A',    label: '주문 반품상태 변경' },
  { group: '주문', event_no: 90028, path: null,                   tier: 'TODO', label: '주문 교환상태 변경 (미구현)' },
  { group: '주문', event_no: 90029, path: '/cafe24wh/order/new',  tier: 'A',    label: '주문 환불상태 변경' },
  { group: '주문', event_no: 90031, path: null,                   tier: 'TODO', label: '주문 상품 추가 (미구현)' },
  { group: '주문', event_no: 90064, path: null,                   tier: 'TODO', label: '주문 수령자정보 변경 (미구현)' },
  { group: '주문', event_no: 90066, path: null,                   tier: 'TODO', label: '주문 관리자메모 등록 (미구현)' },
  { group: '주문', event_no: 90068, path: null,                   tier: 'TODO', label: '주문 관리자메모 수정 (미구현)' },
  { group: '주문', event_no: 90069, path: null,                   tier: 'TODO', label: '주문 관리자메모 삭제 (미구현)' },
  { group: '주문', event_no: 90070, path: null,                   tier: 'TODO', label: '주문서 삭제 (미구현)' },
  { group: '주문', event_no: 90071, path: null,                   tier: 'TODO', label: '주문 배송상태 변경 (일괄·미구현)' },
  { group: '주문', event_no: 90072, path: null,                   tier: 'TODO', label: '주문 취소상태 변경 (일괄·미구현)' },
  { group: '주문', event_no: 90073, path: null,                   tier: 'TODO', label: '주문 환불상태 변경 (일괄·미구현)' },
  { group: '주문', event_no: 90074, path: null,                   tier: 'TODO', label: '주문 반품상태 변경 (일괄·미구현)' },
  { group: '주문', event_no: 90084, path: '/cafe24wh/order/new',  tier: 'A',    label: '상품 장바구니 담김' },
  { group: '주문', event_no: 90160, path: null,                   tier: 'TODO', label: '주문 반품 완료 (취소번호별·미구현)' },
  { group: '주문', event_no: 90162, path: null,                   tier: 'TODO', label: '주문 송장번호 변경 (미구현)' },
  { group: '주문', event_no: 90186, path: null,                   tier: 'TODO', label: '주문 취소상태 변경 (품목별·미구현)' },
  { group: '주문', event_no: 90188, path: null,                   tier: 'TODO', label: '주문 상품 추가 (품목별·미구현)' },

  // ---- 앱 ----
  { group: '앱', event_no: 90077, path: '/cafe24wh/app/remove',           tier: 'A',   label: '앱 삭제' },
  { group: '앱', event_no: 90078, path: '/cafe24wh/app/expire',           tier: 'B',   label: '앱 만료' },
  { group: '앱', event_no: 90079, path: '/cafe24wh/app/expire_extended',  tier: 'B',   label: '앱 만료일 연장' },
  { group: '앱', event_no: 90157, path: '/cafe24wh/app/pay_successful',   tier: 'PAY', label: '앱 결제 (결제 모델 미확정)' },
  { group: '앱', event_no: 90158, path: '/cafe24wh/app/refund',           tier: 'PAY', label: '앱 결제 환불 요청 (결제 모델 미확정)' },
  { group: '앱', event_no: 90159, path: '/cafe24wh/app/refund_successful', tier: 'PAY', label: '앱 결제 환불 완료 (결제 모델 미확정)' },
];

const GROUP_ORDER = ['상품', '게시판', '주문', '앱'];
const GROUP_ICON  = { '상품': '📦', '게시판': '📝', '주문': '🛒', '앱': '📲' };

@inject(({ webhook }) => ({
  getWebhookStats: webhook.getWebhookStats,
  getWebhookTrend: webhook.getWebhookTrend,
  getWebhookEventBreakdown: webhook.getWebhookEventBreakdown,
  getWebhookStoreBreakdown: webhook.getWebhookStoreBreakdown,
  getRecentFailedWebhooks: webhook.getRecentFailedWebhooks,
  getWebhookLogDetail: webhook.getWebhookLogDetail,
  getWebhookCoverage: webhook.getWebhookCoverage,
  retryWebhook: webhook.retryWebhook,
  resetWebhookLogs: webhook.resetWebhookLogs,
}))
@inject(({ AOA }) => ({ getStoreList: AOA.getStoreList, csLogin: AOA.csLogin }))
@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@observer
class WebhookDashboard extends Component {

  @observable isLoading = true;
  @observable period = '7d';
  @observable startDate = '';
  @observable endDate = '';
  @observable granularity = 'day';   // 'day' | 'hour'
  @observable storeKeyword = '';
  @observable selectedStoreId = '';
  @observable selectedMallId = '';
  @observable storeOptions = [];
  @observable stats = {
    totalReceived: 0, succeeded: 0, skipped: 0, failed: 0, dead: 0,
    successRate: 0, avgProcessingMs: 0, avgAckMs: 0,
    todayCount: 0, last7dCount: 0, ackSlaViolations: 0
  };
  @observable trend = [];
  @observable eventBreakdown = [];
  @observable storeBreakdown = [];
  @observable recentFailed = [];
  @observable coverage = [];        // routePath별 수신 집계 (등록 점검용)
  @observable coverageOpen = { '상품': false, '게시판': false, '주문': false, '앱': false };
  @observable detailDoc = null;
  @observable detailOpen = false;
  @observable retryingIds = {};

  constructor(props) {
    super(props);
    this.trendChartRef = React.createRef();
    this.trendChartInstance = null;
  }

  componentDidMount() {
    const me = this.props.me || {};
    if (me.emailId !== 'admin@onnomads.com') {
      toast.error('접근 권한이 없습니다.');
      history.replace('/dashboard');
      return;
    }
    this.applyPeriod('7d');
    this.fetchStoreOptions();
    this.doSearch();
  }

  componentWillUnmount() {
    if (this.trendChartInstance) this.trendChartInstance.destroy();
  }

  @action applyPeriod = (p) => {
    this.period = p;
    const now = moment();
    if (p === 'today') {
      this.startDate = now.clone().startOf('day').format('YYYY-MM-DD');
      this.endDate   = now.clone().add(1, 'day').startOf('day').format('YYYY-MM-DD');
      this.granularity = 'hour';
    } else if (p === '7d') {
      this.startDate = now.clone().subtract(6, 'days').startOf('day').format('YYYY-MM-DD');
      this.endDate   = now.clone().add(1, 'day').startOf('day').format('YYYY-MM-DD');
      this.granularity = 'day';
    } else if (p === '30d') {
      this.startDate = now.clone().subtract(29, 'days').startOf('day').format('YYYY-MM-DD');
      this.endDate   = now.clone().add(1, 'day').startOf('day').format('YYYY-MM-DD');
      this.granularity = 'day';
    }
  };

  @action fetchStoreOptions = async () => {
    if (typeof this.props.getStoreList !== 'function') return;
    const params = { page: 1, limit: 200, sort: 'createdAt', searchParams: this.storeKeyword ? { keyword: this.storeKeyword } : {} };
    try {
      const res = await this.props.getStoreList(params);
      this.storeOptions = (res && res.docs) || [];
    } catch (e) { /* ignore */ }
  };

  @action doSearch = async () => {
    this.isLoading = true;
    const baseArgs = {
      startDate: this.startDate,
      endDate: this.endDate,
      store_id: this.selectedStoreId || undefined,
      mall_id: this.selectedMallId || undefined,
    };
    try {
      // 등록 점검은 항상 30일 윈도우로 (기간 필터 무관)
      const cov30d = {
        startDate: moment().subtract(30, 'days').startOf('day').format('YYYY-MM-DD'),
        endDate: moment().add(1, 'day').startOf('day').format('YYYY-MM-DD')
      };

      const [stats, trend, eb, sb, rf, cov] = await Promise.all([
        this.props.getWebhookStats(baseArgs),
        this.props.getWebhookTrend({ ...baseArgs, granularity: this.granularity }),
        this.props.getWebhookEventBreakdown({ ...baseArgs, limit: 20 }),
        this.props.getWebhookStoreBreakdown({ startDate: this.startDate, endDate: this.endDate, limit: 30 }),
        this.props.getRecentFailedWebhooks({ ...baseArgs, status: 'failed,dead', limit: 50 }),
        this.props.getWebhookCoverage(cov30d),
      ]);
      if (stats && stats.data) this.stats = stats.data;
      this.trend = (trend && trend.data) || [];
      this.eventBreakdown = (eb && eb.data) || [];
      this.storeBreakdown = (sb && sb.data) || [];
      this.recentFailed = (rf && rf.data) || [];
      this.coverage = (cov && cov.data) || [];
      this.autoOpenMissingGroups();
      setTimeout(() => this.renderTrendChart(), 50);
    } catch (e) {
      console.error('webhook dashboard search error', e);
      toast.error('데이터 조회 중 오류가 발생했습니다.');
    } finally {
      this.isLoading = false;
    }
  };

  // 웹훅 로그 리셋 — 현재 적용된 기간(startDate/endDate) 만 삭제 (없으면 전체)
  resetLogs = async () => {
    const periodLabel = (this.startDate && this.endDate)
      ? `${this.startDate} ~ ${this.endDate}`
      : '전체 기간';
    const cf = window.confirm(
      `웹훅 로그를 삭제합니다.\n\n대상: ${periodLabel}\n\n이 작업은 되돌릴 수 없습니다. 정말 진행할까요?`
    );
    if (!cf) return;
    try {
      this.isLoading = true;
      const res = await this.props.resetWebhookLogs({
        startDate: this.startDate || undefined,
        endDate: this.endDate || undefined
      });
      const deleted = (res && res.data && res.data.deletedCount) || 0;
      toast.success(`웹훅 로그 ${deleted.toLocaleString()}건이 삭제되었습니다.`);
      await this.doSearch();
    } catch (e) {
      console.error('resetWebhookLogs error', e);
      toast.error('웹훅 로그 삭제에 실패했습니다.');
    } finally {
      this.isLoading = false;
    }
  };

  // mall_id 로 CS 로그인 → reviewList 로 이동 (allStoreList 의 csLogin 패턴과 동일)
  csLoginByMallId = async (mallId) => {
    if (!mallId) return;
    try {
      await this.props.csLogin({ platformId: mallId });
      try { window.localStorage.removeItem('shops'); } catch (e) {}
      window.location.href = '/reviewList';
    } catch (e) {
      console.error('csLogin error', e);
      toast.error('로그인에 실패했습니다.');
    }
  };

  // Top 30 에서 mall_id 선택 → shopList 로 이동하지 않고 이 페이지에서 해당 몰을 상세 조회
  selectMall = (mallId) => {
    if (!mallId) return;
    this.selectedMallId = mallId;
    this.selectedStoreId = '';   // 스토어 셀렉트와 상호배타 (mall 선택 시 스토어 필터 해제)
    this.doSearch();
    try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) {}
  };

  renderTrendChart = () => {
    if (!this.trendChartRef.current) return;
    if (this.trendChartInstance) this.trendChartInstance.destroy();
    const labels = (this.trend || []).map(r => r.bucket);
    const received = (this.trend || []).map(r => r.received || 0);
    const succeeded = (this.trend || []).map(r => r.succeeded || 0);
    const failed = (this.trend || []).map(r => r.failed || 0);

    this.trendChartInstance = new Chart(this.trendChartRef.current, {
      type: 'line',
      data: {
        labels,
        datasets: [
          { label: '수신', data: received, borderColor: '#5a6af0', backgroundColor: 'rgba(90,106,240,0.1)', tension: 0.3, fill: true },
          { label: '성공', data: succeeded, borderColor: '#10b981', backgroundColor: 'rgba(16,185,129,0.1)', tension: 0.3, fill: false },
          { label: '실패', data: failed, borderColor: '#dc2626', backgroundColor: 'rgba(220,38,38,0.1)', tension: 0.3, fill: false },
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'top' } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
      }
    });
  };

  @action onRetry = async (logId) => {
    if (!logId) return;
    this.retryingIds[logId] = true;
    try {
      const res = await this.props.retryWebhook({ _id: logId });
      if (res && res.data) toast.success('재처리 큐에 등록되었습니다.');
      else toast.error('재처리 실패');
      setTimeout(() => this.doSearch(), 1500);
    } catch (e) {
      toast.error('재처리 실패');
    } finally {
      this.retryingIds[logId] = false;
    }
  };

  @action toggleCoverageGroup = (groupName) => {
    this.coverageOpen[groupName] = !this.coverageOpen[groupName];
  };

  // 검색 직후 미수신(점검 필요) 그룹은 자동 펼침, 모두 활성인 그룹은 접힘
  @action autoOpenMissingGroups = () => {
    const covMap = {};
    (this.coverage || []).forEach(r => {
      if (!r.routePath) return;
      if (r.event_no) covMap[r.routePath + '|' + r.event_no] = r;
    });
    const isReceived = (c) => {
      const cov = c.path && c.event_no ? covMap[c.path + '|' + c.event_no] : null;
      return !!(cov && cov.count > 0);
    };
    GROUP_ORDER.forEach(g => {
      const groupItems = HANDLER_CATALOG.filter(c => c.group === g);
      const hasMissing = groupItems.some(c => c.path && (c.tier === 'A' || c.tier === 'B') && !isReceived(c));
      this.coverageOpen[g] = hasMissing;
    });
  };

  @action openDetail = async (logId) => {
    try {
      const res = await this.props.getWebhookLogDetail({ _id: logId });
      this.detailDoc = (res && res.data) || null;
      this.detailOpen = true;
    } catch (e) {
      toast.error('상세 조회 실패');
    }
  };

  @action closeDetail = () => {
    this.detailOpen = false;
    this.detailDoc = null;
  };

  renderKpiCard(label, value, sub, color) {
    return (
      <Col lg="3" md="6" sm="12" style={{marginBottom: 12}}>
        <Card>
          <CardBody style={{padding: '16px 18px'}}>
            <div style={{fontSize: 12, color: '#868e96', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3}}>{label}</div>
            <div style={{fontSize: 26, fontWeight: 700, color: color || '#212529', marginTop: 6}}>{value}</div>
            {sub && <div style={{fontSize: 11, color: '#6c757d', marginTop: 4}}>{sub}</div>}
          </CardBody>
        </Card>
      </Col>
    );
  }

  renderStatusBadge(status) {
    const colorMap = {
      received: '#6c757d', queued: '#6c757d', processing: '#0ea5e9',
      succeeded: '#10b981', skipped: '#ca8a04', failed: '#dc2626', dead: '#7c2d12'
    };
    return (
      <Badge style={{backgroundColor: colorMap[status] || '#6c757d', color: '#fff', fontSize: 11}}>{status}</Badge>
    );
  }

  // === Webhook 등록 점검 ===
  // 그룹(상품/게시판/주문/앱) 별로 카드 1장씩 노출. event_no 단위 매칭.
  renderCoverageCard() {
    // (routePath, event_no) tuple → coverage row 매핑
    const covMap = {};                          // key = `${path}|${event_no}`
    const covMapByPath = {};                    // event_no 누락 폴백용 (path 단위)
    (this.coverage || []).forEach(r => {
      if (!r.routePath) return;
      if (r.event_no) {
        covMap[r.routePath + '|' + r.event_no] = r;
      }
      if (!covMapByPath[r.routePath]) covMapByPath[r.routePath] = [];
      covMapByPath[r.routePath].push(r);
    });

    const knownKeys = new Set(HANDLER_CATALOG.map(c => (c.path || '∅') + '|' + c.event_no));
    const inferGroup = (path) => {
      if (!path) return '앱';
      if (path.indexOf('/cafe24wh/product') === 0 || path.indexOf('/cafe24wh/category') === 0) return '상품';
      if (path.indexOf('/cafe24wh/article') === 0 || path.indexOf('/cafe24wh/comment') === 0) return '게시판';
      if (path.indexOf('/cafe24wh/order') === 0) return '주문';
      return '앱';
    };
    // 카탈로그에 없는 (path, event_no) 조합은 미지의 핸들러로 별도 추가
    const unknownItems = (this.coverage || [])
      .filter(r => r.routePath && r.event_no && !knownKeys.has(r.routePath + '|' + r.event_no))
      .map(r => ({ group: inferGroup(r.routePath), event_no: r.event_no, path: r.routePath, tier: 'UNK', label: '⚠️ 카탈로그 외 — 코드/등록 검토' }));

    const merged = HANDLER_CATALOG.concat(unknownItems).map(c => {
      let cov = c.path && c.event_no ? covMap[c.path + '|' + c.event_no] : null;
      // event_no 없는 webhook 이 들어왔을 가능성 — path 단위 fallback (event_no 없는 행)
      if (!cov && c.path && covMapByPath[c.path]) {
        const noEventRow = covMapByPath[c.path].find(r => !r.event_no);
        if (noEventRow) cov = noEventRow;
      }
      const received = !!(cov && cov.count > 0);
      return { ...c, cov, received };
    });

    // 그룹별 분류 + event_no 오름차순
    const grouped = { '상품': [], '게시판': [], '주문': [], '앱': [] };
    merged.forEach(r => { (grouped[r.group] || grouped['앱']).push(r); });
    GROUP_ORDER.forEach(g => {
      grouped[g].sort((a, b) => (a.event_no || 0) - (b.event_no || 0));
    });

    const tierBadge = (t) => {
      const map = { A: '#5a6af0', B: '#10b981', PAY: '#f59e0b', TODO: '#9ca3af', UNK: '#dc2626' };
      const labels = { A: 'Tier A', B: 'Tier B', PAY: '결제 보류', TODO: '미구현', UNK: '카탈로그 외' };
      return <Badge style={{backgroundColor: map[t] || '#6c757d', color:'#fff', fontSize: 10}}>{labels[t] || t}</Badge>;
    };

    // 등록 점검 대상: 코드는 있는데(path 있고 tier가 A/B) 30일 내 수신이 없는 항목
    const isMissing = (r) => !!r.path && (r.tier === 'A' || r.tier === 'B') && !r.received;
    const totalMissing = merged.filter(isMissing).length;

    const renderRows = (rows) => rows.map((r, i) => {
      const cov = r.cov;
      const missing = isMissing(r);
      return (
        <tr key={i} style={{borderTop: '1px solid #f0f0f0', backgroundColor: missing ? '#fff7f7' : 'transparent'}}>
          <td style={{padding: 8, whiteSpace: 'nowrap'}}>
            <div style={{fontWeight: 600}}>{r.event_no}</div>
            {CAFE24_EVENT_NAMES[r.event_no] && (
              <div style={{fontSize: 10, color:'#6c757d', fontWeight: 400, whiteSpace: 'normal', maxWidth: 240}}>
                {CAFE24_EVENT_NAMES[r.event_no]}
              </div>
            )}
          </td>
          <td style={{padding: 8}}>{tierBadge(r.tier)}</td>
          <td style={{padding: 8, fontFamily: 'monospace', fontSize: 11, color: r.path ? '#212529' : '#adb5bd'}}>{r.path || '— (코드 없음)'}</td>
          <td style={{padding: 8}}>{r.label}</td>
          <td style={{padding: 8, textAlign:'right'}}>{cov ? (cov.count || 0).toLocaleString() : 0}</td>
          <td style={{padding: 8, textAlign:'right'}}>{cov ? (cov.succeeded || 0) : 0}</td>
          <td style={{padding: 8, textAlign:'right'}}>{cov ? (cov.failed || 0) : 0}</td>
          <td style={{padding: 8, color:'#6c757d'}}>{cov && cov.lastReceivedAt ? moment(cov.lastReceivedAt).fromNow() : '-'}</td>
          <td style={{padding: 8}}>
            {missing
              ? <Badge style={{backgroundColor:'#dc2626', color:'#fff', fontSize: 10}}>등록 점검 필요</Badge>
              : r.received
                ? <Badge style={{backgroundColor:'#10b981', color:'#fff', fontSize: 10}}>활성</Badge>
                : <Badge style={{backgroundColor:'#9ca3af', color:'#fff', fontSize: 10}}>{r.tier === 'PAY' ? '결제 보류' : (r.tier === 'UNK' ? '카탈로그 외' : '미구현')}</Badge>
            }
          </td>
        </tr>
      );
    });

    const renderGroupTable = (groupName) => {
      const rows = grouped[groupName] || [];
      const groupMissing = rows.filter(isMissing).length;
      const totalRows = rows.length;
      const activeRows = rows.filter(r => r.received).length;
      const isOpen = !!this.coverageOpen[groupName];

      return (
        <Card className="mb-2" key={groupName}>
          <CardHeader
            style={{padding: '12px 16px', display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer', userSelect:'none'}}
            onClick={() => this.toggleCoverageGroup(groupName)}
          >
            <div style={{display:'flex', alignItems:'center', gap: 8}}>
              <span style={{fontSize: 14, color:'#6c757d', display:'inline-block', width: 14}}>{isOpen ? '▾' : '▸'}</span>
              <b>{GROUP_ICON[groupName]} {groupName}</b>
              <span style={{fontSize: 11, color:'#868e96'}}>총 {totalRows}건 · 활성 {activeRows}</span>
            </div>
            <Badge style={{backgroundColor: groupMissing > 0 ? '#dc2626' : '#10b981', color:'#fff'}}>
              {groupMissing > 0 ? `미수신 ${groupMissing}건 점검` : '모두 수신 중 ✓'}
            </Badge>
          </CardHeader>
          {isOpen && (
            <CardBody style={{padding: 0}}>
              <table className="s-table" style={{width: '100%', fontSize: 12}}>
                <thead><tr style={{backgroundColor: '#f8f9fc'}}>
                  <th style={{padding: 8}}>event_no</th>
                  <th style={{padding: 8}}>Tier</th>
                  <th style={{padding: 8}}>Path</th>
                  <th style={{padding: 8}}>설명</th>
                  <th style={{padding: 8, textAlign:'right'}}>30일 수신</th>
                  <th style={{padding: 8, textAlign:'right', color:'#10b981'}}>성공</th>
                  <th style={{padding: 8, textAlign:'right', color:'#dc2626'}}>실패</th>
                  <th style={{padding: 8}}>마지막 수신</th>
                  <th style={{padding: 8}}>상태</th>
                </tr></thead>
                <tbody>
                {renderRows(rows)}
                {rows.length === 0 && (
                  <tr><td colSpan={9} style={{padding: 16, textAlign:'center', color:'#adb5bd'}}>이벤트 없음</td></tr>
                )}
                </tbody>
              </table>
            </CardBody>
          )}
        </Card>
      );
    };

    return (
      <div>
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 8}}>
          <div style={{fontSize: 13, fontWeight: 600, color: '#212529'}}>
            <span role="img" aria-label="돋보기">🔍</span> Webhook 등록 점검
          </div>
          <Badge style={{backgroundColor: totalMissing > 0 ? '#dc2626' : '#10b981', color:'#fff'}}>
            전체 {totalMissing > 0 ? `미수신 ${totalMissing}건` : '모두 수신 중 ✓'}
          </Badge>
        </div>
        {GROUP_ORDER.map(g => renderGroupTable(g))}
      </div>
    );
  }

  render() {
    const s = this.stats || {};
    return (
      <div style={{padding: '0px 16px 30px'}}>
        <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8}}>
          <PageTitle title="Webhook 모니터" subtitle="스토어 관리" className="mb-4"/>
          <Button size="sm" theme="danger" outline onClick={this.resetLogs} disabled={this.isLoading}
                  title="현재 적용된 기간(또는 전체)의 웹훅 로그를 삭제합니다.">
            <span role="img" aria-label="warning" style={{marginRight: 4}}>⚠️</span>
            웹훅 로그 리셋
          </Button>
        </div>

        {/* 필터 바 */}
        <Card className="mb-3"><CardBody style={{padding: 14}}>
          <div style={{display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center'}}>
            <ButtonGroup size="sm">
              {['today','7d','30d'].map(p => (
                <Button key={p} theme={this.period===p?'primary':'light'} outline={this.period!==p}
                        onClick={() => { this.applyPeriod(p); this.doSearch(); }}>
                  {p === 'today' ? '오늘' : p === '7d' ? '7일' : '30일'}
                </Button>
              ))}
            </ButtonGroup>
            <FormSelect size="sm" value={this.granularity}
                        onChange={e => { this.granularity = e.target.value; this.doSearch(); }}
                        style={{maxWidth: 120}}>
              <option value="day">일별</option>
              <option value="hour">시간별</option>
            </FormSelect>
            <FormSelect size="sm" value={this.selectedStoreId}
                        onChange={e => { this.selectedStoreId = e.target.value; this.selectedMallId=''; this.doSearch(); }}
                        style={{maxWidth: 240}}>
              <option value="">스토어 전체</option>
              {(this.storeOptions || []).map(s => (
                <option key={s._id} value={s._id}>{s.name || s.platformId}</option>
              ))}
            </FormSelect>
            <FormInput size="sm" placeholder="mall_id 직접 입력" value={this.selectedMallId}
                       onChange={e => this.selectedMallId = e.target.value}
                       onBlur={() => this.doSearch()} style={{maxWidth: 200}}/>
            <Button size="sm" theme="secondary" onClick={() => this.doSearch()}>새로고침</Button>
          </div>
        </CardBody></Card>

        {/* KPI */}
        <Row>
          {this.renderKpiCard('오늘 수신', (s.todayCount||0).toLocaleString(), null, '#5a6af0')}
          {this.renderKpiCard('7일 수신', (s.last7dCount||0).toLocaleString(), null, '#5a6af0')}
          {this.renderKpiCard('성공률', (s.successRate||0).toFixed(1) + '%',
            (s.succeeded||0)+' 성공 / '+((s.failed||0)+(s.dead||0))+' 실패', '#10b981')}
          {this.renderKpiCard('평균 처리시간', (s.avgProcessingMs||0).toLocaleString() + ' ms',
            'ACK 평균 ' + (s.avgAckMs||0) + 'ms', '#06b6d4')}
        </Row>
        <Row>
          {this.renderKpiCard('수신 총합', (s.totalReceived||0).toLocaleString(),
            'skipped ' + (s.skipped||0), '#212529')}
          {this.renderKpiCard('실패', ((s.failed||0)+(s.dead||0)).toLocaleString(),
            'dead ' + (s.dead||0) + ' / failed ' + (s.failed||0), '#dc2626')}
          {this.renderKpiCard('5초 ACK 위반', (s.ackSlaViolations||0).toLocaleString(),
            'ackedIn200Ms > 5000', '#f59e0b')}
        </Row>

        {/* 시계열 차트 */}
        <Card className="mb-3">
          <CardHeader style={{padding: '12px 16px'}}><b>시계열</b> ({this.granularity === 'hour' ? '시간별' : '일별'})</CardHeader>
          <CardBody style={{padding: 14, height: 280}}>
            <canvas ref={this.trendChartRef}/>
          </CardBody>
        </Card>

        {/* 이벤트 + 스토어 분포 */}
        <Row style={{alignItems: 'stretch'}}>
          <Col md="7" style={{display: 'flex'}}>
            <Card className="mb-3" style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
              <CardHeader style={{padding: '12px 16px'}}><b>이벤트별 분포</b> (Top 20)</CardHeader>
              <CardBody style={{padding: 0, flex: 1, overflowY: 'auto'}}>
                <table className="s-table" style={{width: '100%', fontSize: 12}}>
                  <thead><tr style={{backgroundColor: '#f8f9fc'}}>
                    <th style={{padding: 8}}>event_no</th>
                    <th style={{padding: 8}}>event_code</th>
                    <th style={{padding: 8}}>resource</th>
                    <th style={{padding: 8, textAlign:'right'}}>총</th>
                    <th style={{padding: 8, textAlign:'right', color:'#10b981'}}>성공</th>
                    <th style={{padding: 8, textAlign:'right', color:'#dc2626'}}>실패</th>
                    <th style={{padding: 8, textAlign:'right', color:'#ca8a04'}}>skip</th>
                  </tr></thead>
                  <tbody>
                  {(this.eventBreakdown || []).map((r, i) => (
                    <tr key={i} style={{borderTop: '1px solid #f0f0f0'}}>
                      <td style={{padding: 8}}>{r.event_no || '-'}</td>
                      <td style={{padding: 8}}>{r.event_code || '-'}</td>
                      <td style={{padding: 8}}>{r.resource_kind || '-'}</td>
                      <td style={{padding: 8, textAlign:'right'}}>{(r.count||0).toLocaleString()}</td>
                      <td style={{padding: 8, textAlign:'right'}}>{r.succeeded||0}</td>
                      <td style={{padding: 8, textAlign:'right'}}>{r.failed||0}</td>
                      <td style={{padding: 8, textAlign:'right'}}>{r.skipped||0}</td>
                    </tr>
                  ))}
                  {(this.eventBreakdown||[]).length === 0 && (
                    <tr><td colSpan={7} style={{padding: 16, textAlign:'center', color:'#adb5bd'}}>데이터 없음</td></tr>
                  )}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </Col>
          <Col md="5" style={{display: 'flex'}}>
            <Card className="mb-3" style={{flex: 1, display: 'flex', flexDirection: 'column'}}>
              <CardHeader style={{padding: '12px 16px'}}><b>스토어 Top 30</b></CardHeader>
              <CardBody style={{padding: 0, flex: 1, overflowY: 'auto', minHeight: 0}}>
                <table className="s-table" style={{width: '100%', fontSize: 12}}>
                  <thead><tr style={{backgroundColor: '#f8f9fc', position: 'sticky', top: 0}}>
                    <th style={{padding: 8}}>mall_id</th>
                    <th style={{padding: 8}}>플랜</th>
                    <th style={{padding: 8}}>만료일</th>
                    <th style={{padding: 8, textAlign:'right'}}>수신</th>
                    <th style={{padding: 8, textAlign:'right', color:'#dc2626'}}>실패</th>
                  </tr></thead>
                  <tbody>
                  {(this.storeBreakdown || []).map((r, i) => (
                    <tr key={i} style={{borderTop: '1px solid #f0f0f0', backgroundColor: this.selectedMallId && this.selectedMallId === r.mall_id ? '#eef1fd' : 'transparent'}}>
                      <td style={{padding: 8, whiteSpace: 'nowrap'}}>
                        {r.mall_id ? (
                          <button type="button"
                             onClick={() => this.selectMall(r.mall_id)}
                             style={{
                               color: this.selectedMallId === r.mall_id ? '#1b3bbb' : '#5664D2',
                               fontWeight: this.selectedMallId === r.mall_id ? 700 : 400,
                               background: 'none', border: 'none', padding: 0, font: 'inherit',
                               textDecoration: 'none', borderBottom: '1px dashed #c9d2e3', cursor: 'pointer'
                             }}
                             title="이 몰을 선택해 상세 조회">
                            {r.mall_id}
                          </button>
                        ) : '(no mall)'}
                        {r.mall_id && (
                          <Button size="sm" theme="light"
                                  style={{fontSize: '10px', padding: '1px 6px', marginLeft: 6, lineHeight: 1.3}}
                                  onClick={() => this.csLoginByMallId(r.mall_id)}>
                            login
                          </Button>
                        )}
                        {r.mall_id && (
                          <a href={`/shopList?keyword=${encodeURIComponent(r.mall_id)}`}
                             target="_blank" rel="noopener noreferrer"
                             title="샵 리스트에서 검색 (새 탭)"
                             style={{marginLeft: 6, color: '#5664D2', verticalAlign: 'middle', display: 'inline-flex'}}>
                            <FeatherIcon icon="search" size="14"/>
                          </a>
                        )}
                      </td>
                      <td style={{padding: 8, fontSize: 11, color: r.isPaidUser ? '#10b981' : (r.isFreeStore ? '#f59e0b' : '#94a3b8')}}>
                        {r.plan || '-'}
                      </td>
                      <td style={{padding: 8, fontSize: 11, color: '#64748b', whiteSpace: 'nowrap'}}>
                        {r.automaticPayment === 'T' ? (
                          <span style={{color: '#5664D2', fontWeight: 600}}
                                title={r.useExpiryDate ? moment(r.useExpiryDate).format('YYYY-MM-DD') : ''}>
                            정기 결제
                          </span>
                        ) : (r.useExpiryDate ? (() => {
                          const exp = moment(r.useExpiryDate);
                          const diff = exp.diff(moment().startOf('day'), 'days');
                          const dLabel = diff >= 0 ? `D-${diff}` : `D+${Math.abs(diff)}`;
                          const color = diff < 0 ? '#dc2626' : (diff <= 7 ? '#f59e0b' : '#64748b');
                          return (
                            <span title={exp.format('YYYY-MM-DD')}>
                              {exp.format('YY.MM.DD')}
                              <span style={{marginLeft: 4, color, fontWeight: 600}}>{dLabel}</span>
                            </span>
                          );
                        })() : '-')}
                      </td>
                      <td style={{padding: 8, textAlign:'right'}}>{(r.count||0).toLocaleString()}</td>
                      <td style={{padding: 8, textAlign:'right'}}>{r.failed||0}</td>
                    </tr>
                  ))}
                  {(this.storeBreakdown||[]).length === 0 && (
                    <tr><td colSpan={5} style={{padding: 16, textAlign:'center', color:'#adb5bd'}}>데이터 없음</td></tr>
                  )}
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* 최근 실패 테이블 */}
        <Card className="mb-3">
          <CardHeader style={{padding: '12px 16px'}}><b>최근 실패 webhook</b> (Top 50)</CardHeader>
          <CardBody style={{padding: 0, maxHeight: 480, overflowY: 'auto'}}>
            <table className="s-table" style={{width: '100%', fontSize: 12}}>
              <thead><tr style={{backgroundColor: '#f8f9fc', position: 'sticky', top: 0}}>
                <th style={{padding: 8}}>시각</th>
                <th style={{padding: 8}}>mall_id</th>
                <th style={{padding: 8}}>이벤트</th>
                <th style={{padding: 8}}>path</th>
                <th style={{padding: 8}}>상태</th>
                <th style={{padding: 8, textAlign:'right'}}>시도</th>
                <th style={{padding: 8}}>에러</th>
                <th style={{padding: 8}}>액션</th>
              </tr></thead>
              <tbody>
              {(this.recentFailed || []).map((r) => (
                <tr key={r._id} style={{borderTop: '1px solid #f0f0f0'}}>
                  <td style={{padding: 8, whiteSpace: 'nowrap'}}>{r.receivedAt ? moment(r.receivedAt).format('MM-DD HH:mm:ss') : '-'}</td>
                  <td style={{padding: 8}}>{r.mall_id || '-'}</td>
                  <td style={{padding: 8}}>{r.event_no || '-'} <span style={{color:'#868e96'}}>{r.event_code ? '/'+r.event_code : ''}</span></td>
                  <td style={{padding: 8, fontFamily:'monospace', fontSize: 11}}>{r.routePath}</td>
                  <td style={{padding: 8}}>{this.renderStatusBadge(r.status)}</td>
                  <td style={{padding: 8, textAlign:'right'}}>{r.attemptCount || 0}</td>
                  <td style={{padding: 8, color:'#dc2626'}} title={r.lastError || ''}>{(r.lastError || '').substr(0, 80)}</td>
                  <td style={{padding: 8, whiteSpace: 'nowrap'}}>
                    <Button size="sm" theme="primary" outline disabled={!!this.retryingIds[r._id]}
                            onClick={() => this.onRetry(r._id)} style={{marginRight: 4}}>재처리</Button>
                    <Button size="sm" theme="secondary" outline onClick={() => this.openDetail(r._id)}>상세</Button>
                  </td>
                </tr>
              ))}
              {(this.recentFailed||[]).length === 0 && (
                <tr><td colSpan={8} style={{padding: 24, textAlign:'center', color:'#adb5bd'}}>실패한 webhook 없음 ✓</td></tr>
              )}
              </tbody>
            </table>
          </CardBody>
        </Card>

        {/* Webhook 등록 점검 (페이지 하단) */}
        {this.renderCoverageCard()}

        {/* 상세 모달 */}
        <Modal size="lg" open={this.detailOpen} toggle={() => this.closeDetail()}>
          <ModalHeader toggle={() => this.closeDetail()}>Webhook 상세</ModalHeader>
          <ModalBody>
            {this.detailDoc ? (
              <div style={{fontSize: 12, fontFamily: 'monospace'}}>
                <div><b>routePath:</b> {this.detailDoc.routePath}</div>
                <div><b>event_no:</b> {this.detailDoc.event_no} / <b>event_code:</b> {this.detailDoc.event_code}</div>
                <div><b>mall_id:</b> {this.detailDoc.mall_id} / <b>shopNo:</b> {this.detailDoc.event_shop_no}</div>
                <div><b>status:</b> {this.detailDoc.status} ({this.detailDoc.attemptCount||0} attempts)</div>
                <div><b>delivery_id:</b> {this.detailDoc.delivery_id || '(none)'}</div>
                <div><b>signaturePresent:</b> {String(this.detailDoc.signaturePresent)} / <b>valid:</b> {String(this.detailDoc.signatureValid)}</div>
                <div><b>receivedAt:</b> {this.detailDoc.receivedAt ? moment(this.detailDoc.receivedAt).format('YYYY-MM-DD HH:mm:ss.SSS') : '-'}</div>
                <div><b>processedAt:</b> {this.detailDoc.processedAt ? moment(this.detailDoc.processedAt).format('YYYY-MM-DD HH:mm:ss.SSS') : '-'}</div>
                <div><b>processingDurationMs:</b> {this.detailDoc.processingDurationMs} / <b>ackedIn200Ms:</b> {this.detailDoc.ackedIn200Ms}</div>
                <hr/>
                <div><b>lastError:</b> <pre style={{whiteSpace:'pre-wrap', color:'#dc2626'}}>{this.detailDoc.lastError || '-'}</pre></div>
                <div><b>lastErrorStack:</b> <pre style={{whiteSpace:'pre-wrap', maxHeight: 200, overflow:'auto'}}>{this.detailDoc.lastErrorStack || '-'}</pre></div>
                <hr/>
                <div><b>headers:</b> <pre style={{whiteSpace:'pre-wrap'}}>{JSON.stringify(this.detailDoc.headers, null, 2)}</pre></div>
                <div><b>payload.resource:</b> <pre style={{whiteSpace:'pre-wrap', maxHeight: 320, overflow:'auto'}}>{JSON.stringify(this.detailDoc.payloadResource || (this.detailDoc.payload && this.detailDoc.payload.resource), null, 2)}</pre></div>
              </div>
            ) : <div>로드중...</div>}
          </ModalBody>
        </Modal>
      </div>
    );
  }
}

export default WebhookDashboard;
